<template>
  <v-card height="100%" :loading="loading" flat color="bg1" class="user-profile">
    <!-- Header Section -->
    <div class="profile-header" :class="{ 'profile-header--dark': $vuetify.theme.isDark }">
      <div class="profile-header__content">
        <div class="profile-header__info">
          <h1 class="profile-header__title">Profile Settings</h1>
          <p class="profile-header__subtitle">Manage your account information and security</p>
        </div>
        <v-btn
          class="logout-btn"
          small
          outlined
          @click="logout">
          <v-icon left small>mdi-logout</v-icon>
          Sign Out
        </v-btn>
      </div>
    </div>

    <v-form ref="profileForm" v-model="valid">
      <div class="profile-content">
        <!-- Profile Card with Avatar -->
        <div class="profile-card profile-card--avatar" :class="{ 'animate-in': !loading }" style="--delay: 0">
          <div class="profile-card__avatar-section">
            <div class="avatar-wrapper" @click="isOwner && triggerUpload()">
              <v-avatar size="120" class="avatar-image elevation-4">
                <v-img v-if="selected.avatarUrl" :src="selected.avatarUrl" />
                <v-icon v-else size="60" color="grey lighten-1">mdi-account</v-icon>
              </v-avatar>
              <div v-if="isOwner" class="avatar-edit-badge">
                <v-icon small color="white">mdi-camera</v-icon>
              </div>
            </div>
            <div class="avatar-info">
              <h2 class="avatar-name">{{ selected.firstName }} {{ selected.lastName }}</h2>
              <p class="avatar-role">{{ selected.email || 'User' }}</p>
              <p v-if="isOwner && !avatarUploadError" class="avatar-hint">Click photo to change</p>
              <p v-if="avatarUploadError" class="avatar-error">
                <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                {{ avatarUploadError }}
              </p>
            </div>
          </div>
          <!-- Hidden uploader -->
          <SingleImageUploader
            ref="imageUploader"
            id="add-uploader"
            :disabled="!isOwner"
            apiUrl="api/users/Image"
            v-model="selected.avatarUrl"
            @error="onAvatarUploadError"
            class="hidden-uploader" />
        </div>

        <!-- Basic Information Section -->
        <div class="profile-card" :class="{ 'animate-in': !loading }" style="--delay: 1">
          <div class="profile-card__header">
            <div class="section-indicator"></div>
            <div class="section-header-content">
              <h3 class="section-title">Basic Information</h3>
              <p class="section-description">Your personal details</p>
            </div>
            <v-btn
              v-if="isOwner"
              :disabled="!canSaveProfile"
              color="primary"
              :loading="updateLoading"
              @click="updateUser"
              class="save-btn save-btn--header"
              depressed
              small>
              <v-icon left small>mdi-content-save</v-icon>
              Save
            </v-btn>
          </div>
          <div class="profile-card__body">
            <v-row>
              <v-col cols="12" sm="6">
                <label class="field-label">First Name</label>
                <v-text-field
                  dense
                  :readonly="!isOwner"
                  outlined
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  v-model="selected.firstName"
                  :rules="[formRules.required]"
                  placeholder="Enter first name"
                  class="custom-field"
                  hide-details="auto">
                  <template v-slot:prepend-inner>
                    <v-icon small color="grey">mdi-account-outline</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="field-label">Last Name</label>
                <v-text-field
                  dense
                  outlined
                  :readonly="!isOwner"
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  v-model="selected.lastName"
                  :rules="[formRules.required]"
                  placeholder="Enter last name"
                  class="custom-field"
                  hide-details="auto">
                  <template v-slot:prepend-inner>
                    <v-icon small color="grey">mdi-account-outline</v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Security Section -->
        <div v-if="isOwner" class="profile-card" :class="{ 'animate-in': !loading }" style="--delay: 2">
          <div class="profile-card__header">
            <div class="section-indicator"></div>
            <div class="section-header-content">
              <h3 class="section-title">Security</h3>
              <p class="section-description">Manage your password and account security</p>
            </div>
          </div>
          <div class="profile-card__body">
            <div class="password-grid">
              <div class="password-field">
                <label class="field-label">Current Password</label>
                <v-text-field
                  dense
                  outlined
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  v-model="passwordForm.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  :append-icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showCurrentPassword = !showCurrentPassword"
                  :rules="[formRules.required]"
                  placeholder="Enter current password"
                  class="custom-field"
                  hide-details="auto">
                </v-text-field>
              </div>
              <div class="password-field">
                <label class="field-label">New Password</label>
                <v-text-field
                  dense
                  outlined
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  v-model="passwordForm.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showNewPassword = !showNewPassword"
                  :rules="[formRules.required, formRules.password, formRules.length(8)]"
                  placeholder="Enter new password"
                  class="custom-field"
                  hide-details="auto">
                
                </v-text-field>
                <div v-if="passwordForm.newPassword" class="password-requirements">
                  <span :class="{ 'met': hasMinLength }">
                    <v-icon x-small :color="hasMinLength ? 'success' : 'grey'">{{ hasMinLength ? 'mdi-check' : 'mdi-circle-small' }}</v-icon>
                    8+ characters
                  </span>
                  <span :class="{ 'met': hasUppercase }">
                    <v-icon x-small :color="hasUppercase ? 'success' : 'grey'">{{ hasUppercase ? 'mdi-check' : 'mdi-circle-small' }}</v-icon>
                    Uppercase
                  </span>
                  <span :class="{ 'met': hasLowercase }">
                    <v-icon x-small :color="hasLowercase ? 'success' : 'grey'">{{ hasLowercase ? 'mdi-check' : 'mdi-circle-small' }}</v-icon>
                    Lowercase
                  </span>
                  <span :class="{ 'met': hasNumber }">
                    <v-icon x-small :color="hasNumber ? 'success' : 'grey'">{{ hasNumber ? 'mdi-check' : 'mdi-circle-small' }}</v-icon>
                    Number
                  </span>
                  <span :class="{ 'met': hasSpecial }">
                    <v-icon x-small :color="hasSpecial ? 'success' : 'grey'">{{ hasSpecial ? 'mdi-check' : 'mdi-circle-small' }}</v-icon>
                    Special char
                  </span>
                </div>
              </div>
              <div class="password-field">
                <label class="field-label">Confirm New Password</label>
                <v-text-field
                  dense
                  outlined
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  v-model="passwordForm.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showConfirmPassword = !showConfirmPassword"
                  :rules="[formRules.required, confirmPasswordRule]"
                  placeholder="Confirm new password"
                  class="custom-field"
                  hide-details="auto">
               
                </v-text-field>
                <div v-if="passwordForm.confirmPassword && passwordForm.newPassword" class="password-match">
                  <v-icon x-small :color="passwordsMatch ? 'success' : 'error'">
                    {{ passwordsMatch ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  <span :class="passwordsMatch ? 'success--text' : 'error--text'">
                    {{ passwordsMatch ? 'Passwords match' : 'Passwords do not match' }}
                  </span>
                </div>
              </div>
            </div>
            <div class="profile-card__actions">
              <v-btn
                :disabled="!isPasswordFormValid"
                color="primary"
                :loading="resetPasswordLoading"
                @click="resetPassword"
                class="save-btn"
                depressed>
                <v-icon left small>mdi-lock-reset</v-icon>
                Update Password
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Active Sessions Section -->
        <ActiveSessions
          v-if="isOwner"
          class="profile-card"
          :class="{ 'animate-in': !loading }"
          style="--delay: 3" />
      </div>
    </v-form>
  </v-card>
</template>

<script>
  import permissions from '@Share/config/permissions';
  import formRules from '@Share/config/form-rules';
  import SingleImageUploader from '@Share/components/SingleImageUploader.vue';
  import ActiveSessions from '@Account/components/ActiveSessions.vue';
  import accountService from '@Account/service/account.service';
  import { $handleError, $stripQueryParams } from '@/utils';

  export default {
    name: 'user-profile',
    data() {
      return {
        permissions,
        formRules,
        selected: { address: {} },
        originalData: null,
        showPassword: false,
        showCPassword: false,
        showCurrentPassword: false,
        showNewPassword: false,
        showConfirmPassword: false,
        valid: false,
        loading: false,
        resetPasswordLoading: false,
        passwordForm: {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        },
        avatarUploadError: null,
      };
    },
    created() {
      this.loading = true;
      this.$store.dispatch('USER/GET_BY_ID', this.$route.params.id).then((res) => {
        this.selected = res.data;
        this.originalData = {
          firstName: res.data.firstName,
          lastName: res.data.lastName,
          avatarUrl: res.data.avatarUrl,
        };
        this.loading = false;
      });
    },
    computed: {
      updateLoading() {
        return this.$store.getters['USER/GET_UPDATE_LOADING'];
      },
      owner() {
        return this.$store.getters['ACCOUNT/GET_USER'];
      },
      isOwner() {
        return this.owner && this.owner.id == this.selected.id;
      },
      hasProfileChanges() {
        if (!this.originalData) return false;
        return (
          this.selected.firstName !== this.originalData.firstName ||
          this.selected.lastName !== this.originalData.lastName ||
          this.selected.avatarUrl !== this.originalData.avatarUrl
        );
      },
      canSaveProfile() {
        const hasRequiredFields = this.selected.firstName?.trim() && this.selected.lastName?.trim();
        return hasRequiredFields && this.hasProfileChanges;
      },
      confirmPasswordRule() {
        return (v) => v === this.passwordForm.newPassword || "Password doesn't match";
      },
      isPasswordFormValid() {
        const { currentPassword, newPassword, confirmPassword } = this.passwordForm;
        if (!currentPassword || !newPassword || !confirmPassword) return false;
        if (newPassword !== confirmPassword) return false;
        if (newPassword.length < 8) return false;
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/;
        return passwordRegex.test(newPassword);
      },
      hasMinLength() {
        return this.passwordForm.newPassword.length >= 8;
      },
      hasUppercase() {
        return /[A-Z]/.test(this.passwordForm.newPassword);
      },
      hasLowercase() {
        return /[a-z]/.test(this.passwordForm.newPassword);
      },
      hasNumber() {
        return /\d/.test(this.passwordForm.newPassword);
      },
      hasSpecial() {
        return /[_\W]/.test(this.passwordForm.newPassword);
      },
      passwordsMatch() {
        return this.passwordForm.newPassword === this.passwordForm.confirmPassword && this.passwordForm.confirmPassword !== '';
      },
    },
    methods: {
      logout() {
        this.$store.dispatch('ACCOUNT/LOGOUT');
      },
      triggerUpload() {
        this.avatarUploadError = null;
        const uploaderInput = this.$refs.imageUploader?.$el?.querySelector('input[type="file"]');
        if (uploaderInput) {
          uploaderInput.click();
        }
      },
      onAvatarUploadError(errorMessage) {
        this.avatarUploadError = errorMessage;
      },
      updateUser() {
        const cleanAvatarUrl = $stripQueryParams(this.selected.avatarUrl);
        const userToUpdate = {
          ...this.selected,
          avatarUrl: cleanAvatarUrl,
        };
        this.$store.dispatch('USER/UPDATE', userToUpdate).then(() => {
          this.$store.commit('ACCOUNT/UPDATE_USER_BASIC', {
            firstName: this.selected.firstName,
            lastName: this.selected.lastName,
            avatarUrl: this.selected.avatarUrl,
          });
          // Update original data to reflect saved state
          this.originalData = {
            firstName: this.selected.firstName,
            lastName: this.selected.lastName,
            avatarUrl: this.selected.avatarUrl,
          };
          // this.$dialog.notify.success('Profile updated successfully');
        });
      },
      resetPassword() {
        this.resetPasswordLoading = true;
        accountService
          .updatePassword({
            currentPassword: this.passwordForm.currentPassword,
            newPassword: this.passwordForm.newPassword,
            confirmPassword: this.passwordForm.confirmPassword,
          })
          .then(() => {
            this.$dialog.notify.success('Password changed successfully');
            // Clear form data
            this.passwordForm = {
              currentPassword: '',
              newPassword: '',
              confirmPassword: '',
            };
            // Reset password visibility toggles
            this.showCurrentPassword = false;
            this.showNewPassword = false;
            this.showConfirmPassword = false;
            // Reset form validation state
            this.$nextTick(() => {
              this.$refs.profileForm?.resetValidation();
              window.location.reload();
            });
          })
          .catch((error) => {
           $handleError(error);
          })
          .finally(() => {
            this.resetPasswordLoading = false;
          });
      },
    },

    components: {
      SingleImageUploader,
      ActiveSessions,
    },
  };
</script>

<style lang="scss" scoped>
.user-profile {
  overflow-y: auto;
}

// Header Styles
.profile-header {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1) 100%);
  padding: 2rem 2.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 60%;
    height: 200%;
    background: radial-gradient(ellipse, rgba(255,255,255,0.1) 0%, transparent 70%);
    pointer-events: none;
  }

  &--dark {
    background: linear-gradient(135deg, var(--v-bg3-base) 0%, var(--v-bg2-base) 100%);

    .profile-header__title {
      color: #fff;
    }

    .profile-header__subtitle {
      color: rgba(255,255,255,0.7);
    }
  }

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #fff;
    margin: 0;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 0.9rem;
    color: rgba(255,255,255,0.85);
    margin: 0.25rem 0 0;
    font-weight: 400;
  }
}

.logout-btn {
  border-color: rgba(255,255,255,0.4) !important;
  color: #fff !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;

  &:hover {
    background: rgba(255,255,255,0.1) !important;
    border-color: rgba(255,255,255,0.6) !important;
  }
}

// Content Area
.profile-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
}

// Card Styles
.profile-card {
  background: var(--v-bg2-base);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04);
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.4s ease, transform 0.4s ease, box-shadow 0.2s ease;

  &.animate-in {
    opacity: 1;
    transform: translateY(0);
    transition-delay: calc(var(--delay) * 0.1s);
  }

  &:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.06);
  }

  &--avatar {
    overflow: hidden;
  }

  &__avatar-section {
    background: linear-gradient(180deg, var(--v-primary-lighten4) 0%, var(--v-bg2-base) 100%);
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    .theme--dark & {
      background: linear-gradient(180deg, rgba(3,98,76,0.15) 0%, var(--v-bg2-base) 100%);
    }
  }

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid rgba(0,0,0,0.06);

    .theme--dark & {
      border-bottom-color: rgba(255,255,255,0.06);
    }
  }

  &__body {
    padding: 1.5rem;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(0,0,0,0.06);

    .theme--dark & {
      border-top-color: rgba(255,255,255,0.06);
    }
  }
}

// Avatar Styles
.avatar-wrapper {
  position: relative;
  margin-bottom: 1rem;
  cursor: pointer;

  &:hover .avatar-edit-badge {
    transform: scale(1.1);
  }
}

.avatar-image {
  background: var(--v-bg1-base);
  border: 3px solid #fff;

  .theme--dark & {
    border-color: var(--v-bg3-base);
  }
}

.avatar-edit-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 32px;
  height: 32px;
  background: var(--v-primary-base);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.avatar-name {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--v-text-base);
}

.avatar-role {
  font-size: 0.85rem;
  color: var(--v-text-secondary);
  margin: 0.25rem 0 0;
  opacity: 0.7;
}

.avatar-hint {
  font-size: 0.75rem;
  color: var(--v-primary-base);
  margin: 0.5rem 0 0;
  opacity: 0.8;
}

.avatar-error {
  font-size: 0.75rem;
  color: var(--v-error-base);
  margin: 0.5rem 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hidden-uploader {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

// Section Styles
.section-indicator {
  width: 4px;
  align-self: stretch;
  background: var(--v-primary-base);
  border-radius: 2px;
  flex-shrink: 0;
}

.section-header-content {
  flex: 1;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: var(--v-text-base);
}

.section-description {
  font-size: 0.85rem;
  color: var(--v-text-secondary);
  margin: 0.25rem 0 0;
  opacity: 0.7;
}

.section-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-text-base);
  display: flex;
  align-items: center;
}

.helper-text {
  font-size: 0.8rem;
  color: var(--v-text-secondary);
  margin: 0.25rem 0 0;
  opacity: 0.7;
}

// Field Styles
.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--v-text-secondary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.custom-field {
  &.v-text-field--outlined {
    border-radius: 8px;

    ::v-deep .v-input__slot {
      min-height: 44px !important;
    }

    ::v-deep fieldset {
      border-color: rgba(0,0,0,0.12);
      transition: border-color 0.2s ease;
    }

    &:hover ::v-deep fieldset {
      border-color: var(--v-primary-lighten2);
    }

    &.v-input--is-focused ::v-deep fieldset {
      border-color: var(--v-primary-base);
      border-width: 2px;
    }
  }
}

// Password Section
.password-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.password-field {
  display: flex;
  flex-direction: column;
}

.password-requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-top: 0.75rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.7rem;
    color: var(--v-text-secondary);
    opacity: 0.6;
    transition: opacity 0.2s ease;

    &.met {
      opacity: 1;
      color: var(--v-success-base);
    }
  }
}

.password-match {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
}

// Button Styles
.save-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  padding: 0 1.5rem !important;
  height: 42px !important;
  border-radius: 8px;

  &:not(:disabled) {
    box-shadow: 0 2px 8px rgba(3, 98, 76, 0.3);
  }

  &--header {
    height: 32px !important;
    padding: 0 1rem !important;
    align-self: center;
  }
}

// Dark mode adjustments
.theme--dark {
  .profile-card {
    box-shadow: 0 1px 3px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15);

    &:hover {
      box-shadow: 0 2px 6px rgba(0,0,0,0.25), 0 8px 24px rgba(0,0,0,0.2);
    }
  }

  .custom-field {
    ::v-deep fieldset {
      border-color: rgba(255,255,255,0.12);
    }
  }

  .logout-btn {
    border-color: rgba(255,255,255,0.3) !important;
  }
}
</style>
